import { ReactNode, useMemo } from 'react';
import { FlowHydrationProvider as ContextProvider } from './context';
import type { FlowHydrationManager } from './types';

/**
 * Глобальный объект window, расширенный состоянием гидрации Flow.
 * @internal
 */
declare const window: {
    /**
     * Flow State - Глобальный Map, содержащий все гидрированные состояния.
     * Заполняется скриптом, сгенерированным на сервере во время SSR.
     */
    _FS_: Map<string, unknown> | undefined;
};

/**
 * Создает клиентский менеджер гидрации для получения состояния, отрендеренного на сервере.
 *
 * Этот менеджер используется в браузере для доступа к состоянию, которое было сериализовано на сервере
 * и встроено в HTML. Он читает из глобальной Map `window._FS_`, который заполняется
 * скриптом, сгенерированным из {@link createServerHydrationManager}.
 *
 * @param options - Параметры конфигурации для менеджера гидрации
 * @returns Экземпляр клиентского менеджера гидрации
 *
 * @example
 * ```tsx
 * import { createFlowHydrationManager, FlowHydrationProvider } from '@tsip/flow-react';
 *
 * const manager = createFlowHydrationManager();
 *
 * function App() {
 *   return (
 *     <FlowHydrationProvider manager={manager}>
 *       <YourApp />
 *     </FlowHydrationProvider>
 *   );
 * }
 * ```
 *
 * @internal
 */
export function createFlowHydrationManager(): FlowHydrationManager {
    return {
        hydrate(id) {
            const state = window._FS_;
            if (state?.has(id)) {
                return { value: state.get(id) };
            }
            return;
        },
        register() {
            // Ничего не делаем на клиенте - регистрация происходит только на сервере
        },
        getScript() {
            // Всегда возвращает null на клиенте - генерация скрипта происходит только на сервере
            return null;
        },
        getNonce() {
            // На клиенте не генерируем скрипты, поэтому nonce не нужен
            return undefined;
        },
    };
}

/**
 * Компонент-провайдер для поддержки гидрации Flow на клиенте.
 *
 * Этот компонент предоставляет контекст с клиентским менеджером гидрации,
 * который позволяет получать состояния, отрендеренные на сервере, и использовать
 * их в браузере при гидрации.
 *
 * @param children - React-компоненты, которые будут обернуты в контекст гидрации
 */
export function FlowHydrationProvider({ children }: { children: ReactNode }) {
    const manager = useMemo(() => createFlowHydrationManager(), []);
    return <ContextProvider manager={manager}>{children}</ContextProvider>;
}
